import { useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import { BuildingComplex, ExternalLink, TriangleAlert } from 'lucide-react'

import TrialEndDateRow from './TrialEndDateRow'

import tabStyles from './ManageUserTab.module.scss'
import styles from '@/pages/app/Users/styles.module.scss'

import { isApiError } from '@/api'
import { fetchUserStatus } from '@/api/enterprise'
import {
  adminLookupUser,
  type AdminUpdateStripeCustomerIdResponse,
  type AdminUserLookupResponse,
  exportUserDictionaryCsv,
  importUserDictionaryCsv,
  updateStripeCustomerId,
} from '@/api/internal'
import { requestResetPassword } from '@/api/user'
import InfoIcon from '@/assets/icons/info.svg?react'
import Button from '@/components/Button'
import ConfirmDialog from '@/components/ConfirmDialog/ConfirmDialog'
import Input from '@/components/Input'
import { useToast } from '@/components/Toast/ToastContext'
import Tooltip from '@/components/Tooltip'
import { useAnalytics } from '@/hooks/useAnalytics'
import { downloadBlob } from '@/utils/csv'
import { EventName } from '@/utils/eventDefinitions'

const formatDate = (unixSeconds: number | null, timeZone?: string) => {
  if (unixSeconds == null) return '—'
  return new Date(unixSeconds * 1000).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    ...(timeZone ? { timeZone } : {}),
  })
}

const PASSWORD_RESET_PROVIDER_LABELS: Record<string, string> = {
  google: 'Google',
  azure: 'Microsoft',
  microsoft: 'Microsoft',
  apple: 'Apple',
  workos: 'SSO',
  sso: 'SSO',
}

const passwordResetUnavailableReason = ({
  enforce_sso,
  email,
  provider,
}: {
  enforce_sso: boolean
  email: string | null
  provider: string | null
}) => {
  if (enforce_sso) {
    return "Password reset is unavailable because this user's organization requires SSO."
  }
  if (!email) {
    return 'Password reset is unavailable because this user has no email address.'
  }
  if (provider && provider !== 'email') {
    const label = PASSWORD_RESET_PROVIDER_LABELS[provider] ?? provider
    return `Password reset is unavailable because this account uses ${label}.`
  }
  return null
}

// plan_name is null for free/no-subscription users; non-null means an active paid plan
const isPaidPlan = (planName: string | null) => planName != null

const passwordResetErrorMessage = (error: unknown) => {
  if (isApiError(error)) {
    if (error.status === 429) {
      return 'Reset request limit reached. Wait before trying again; per-user limits can take up to 30 minutes to clear.'
    }
    return error.detail ?? error.message
  }
  return error instanceof Error ? error.message : "Couldn't request a password reset email. Please try again."
}

const InfoRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div className={tabStyles.infoRow}>
    <span className={tabStyles.infoLabel}>{label}</span>
    <span className={tabStyles.infoValue}>{value}</span>
  </div>
)

const EDIT_TOOLTIP =
  "Overwrite this user's Stripe Customer ID. Dry run previews the change against Stripe; Update applies it. This corrects lookups, the billing portal, and webhook routing — it does not rebuild a missing subscription row."

const StripeCustomerIdRow = ({
  user,
  onApplied,
}: {
  user: AdminUserLookupResponse
  onApplied: () => Promise<void> | void
}) => {
  const { trackEvent } = useAnalytics()

  const [isEditing, setIsEditing] = useState(false)
  const [editValue, setEditValue] = useState('')
  const [dryRunResult, setDryRunResult] = useState<AdminUpdateStripeCustomerIdResponse | null>(null)
  const [editError, setEditError] = useState<string | null>(null)

  const dryRunMutation = useMutation({
    mutationFn: () =>
      updateStripeCustomerId({ user_id: user.user_id, stripe_customer_id: editValue.trim(), dry_run: true }),
    onSuccess: (res) => {
      setDryRunResult(res.data)
      setEditError(null)
    },
    onError: (err) => {
      setDryRunResult(null)
      setEditError(isApiError(err) ? (err.detail ?? err.message) : "Couldn't preview the change. Try again.")
    },
  })

  const applyMutation = useMutation({
    mutationFn: () =>
      updateStripeCustomerId({ user_id: user.user_id, stripe_customer_id: editValue.trim(), dry_run: false }),
    onSuccess: async () => {
      await onApplied()
      trackEvent?.(EventName.UpdatedStripeCustomerId, { user_id: user.user_id })
      exitEditMode()
    },
    onError: (err) => {
      setEditError(isApiError(err) ? (err.detail ?? err.message) : "Couldn't update the Customer ID. Try again.")
    },
  })

  const isPending = dryRunMutation.isPending || applyMutation.isPending

  const enterEditMode = () => {
    setEditValue(user.stripe_customer_id ?? '')
    setDryRunResult(null)
    setEditError(null)
    setIsEditing(true)
  }

  const exitEditMode = () => {
    setIsEditing(false)
    setEditValue('')
    setDryRunResult(null)
    setEditError(null)
  }

  const readonlyValue = user.stripe_customer_id ? (
    user.stripe_customer_id
  ) : isPaidPlan(user.plan_name) ? (
    <span className={tabStyles.missing}>
      <TriangleAlert size={14} /> Missing
    </span>
  ) : (
    'N/A'
  )

  if (!isEditing) {
    return (
      <div className={tabStyles.infoRow}>
        <span className={tabStyles.infoLabel}>Stripe Customer ID</span>
        <span className={tabStyles.infoValue}>{readonlyValue}</span>
        <Button title="Edit" onClick={enterEditMode} type="light" className={tabStyles.rowAction} />
      </div>
    )
  }

  return (
    <div className={tabStyles.editRow}>
      <div className={tabStyles.editHeader}>
        <span className={tabStyles.infoLabel}>Stripe Customer ID</span>
        <Tooltip text={EDIT_TOOLTIP} placement="top">
          <span className={tabStyles.tooltipTrigger} aria-label="About editing the Stripe Customer ID">
            <InfoIcon />
          </span>
        </Tooltip>
      </div>

      <div className={tabStyles.editControls}>
        <Input
          type="text"
          value={editValue}
          onInput={(text) => setEditValue(text)}
          placeholder="cus_..."
          className={tabStyles.editInput}
        />
        <Button
          title="Dry run"
          onClick={() => editValue.trim() && dryRunMutation.mutate()}
          type="light"
          isLoading={dryRunMutation.isPending}
          isDisabled={isPending || !editValue.trim()}
        />
        <Button
          title="Update"
          onClick={() => editValue.trim() && applyMutation.mutate()}
          type="dark"
          isLoading={applyMutation.isPending}
          isDisabled={isPending || !editValue.trim()}
        />
        <Button title="Cancel" onClick={exitEditMode} type="link" isDisabled={isPending} />
      </div>

      {dryRunResult && (
        <div className={tabStyles.dryRunBlock}>
          <div className={tabStyles.dryRunLine}>
            <span className={tabStyles.dryRunLabel}>Stripe customer email</span>
            <span>{dryRunResult.stripe_customer_email ?? '—'}</span>
          </div>
          <div className={tabStyles.dryRunLine}>
            <span className={tabStyles.dryRunLabel}>Change</span>
            <span>
              {dryRunResult.old_stripe_customer_id ?? 'none'} → {dryRunResult.new_stripe_customer_id}
            </span>
          </div>
          {dryRunResult.warnings.length > 0 && (
            <ul className={tabStyles.dryRunWarnings}>
              {dryRunResult.warnings.map((warning, i) => (
                <li key={i}>{warning}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {editError && <p className={styles.error}>{editError}</p>}
    </div>
  )
}

const PasswordResetRow = ({ user }: { user: AdminUserLookupResponse }) => {
  const { updateToast } = useToast()
  const { trackEvent } = useAnalytics()
  // React Query pending state is asynchronous; this blocks same-tick duplicate clicks.
  const requestInFlight = useRef(false)
  const [resetTarget, setResetTarget] = useState<string | null>(null)
  const [resetError, setResetError] = useState<string | null>(null)
  const statusQuery = useQuery({
    queryKey: ['user-status', user.email],
    queryFn: () => fetchUserStatus(user.email ?? ''),
    enabled: Boolean(user.email) && !user.enforce_sso,
  })
  const unavailableReason = passwordResetUnavailableReason({
    enforce_sso: user.enforce_sso,
    email: user.email,
    provider: statusQuery.isSuccess ? (statusQuery.data.data.provider ?? null) : null,
  })
  const resetDisabled = unavailableReason != null || statusQuery.isFetching

  const resetMutation = useMutation({
    mutationFn: async (email: string) => {
      // The admin tool avoids embedding the public forgot-password page's CAPTCHA widget.
      const response = await requestResetPassword({ email })
      if (response.data.error) throw new Error(response.data.message)
      return response
    },
    onSuccess: (_, email) => {
      updateToast(`Password reset email requested for ${email}.`, 'success')
      trackEvent?.(EventName.RequestedUserPasswordReset, { user_id: user.user_id })
      setResetTarget(null)
      setResetError(null)
    },
    onError: (err) => {
      setResetError(passwordResetErrorMessage(err))
    },
    onSettled: () => {
      requestInFlight.current = false
    },
  })

  const openConfirmation = () => {
    if (!user.email || unavailableReason || statusQuery.isFetching) return
    setResetTarget(user.email)
    setResetError(null)
  }

  const closeConfirmation = () => {
    if (resetMutation.isPending) return
    setResetTarget(null)
    setResetError(null)
  }

  const requestReset = () => {
    if (!resetTarget || requestInFlight.current) return
    requestInFlight.current = true
    setResetError(null)
    resetMutation.mutate(resetTarget)
  }

  return (
    <>
      <div className={tabStyles.accountAction}>
        <div className={tabStyles.actionCopy}>
          <span className={tabStyles.infoLabel}>Password reset</span>
          <span className={tabStyles.actionDescription}>
            {unavailableReason ?? 'Send this user a link to choose a new password.'}
          </span>
        </div>
        <Button
          title="Reset password"
          onClick={openConfirmation}
          type="light"
          isDisabled={resetDisabled}
          className={tabStyles.rowAction}
        />
      </div>

      <ConfirmDialog
        isOpen={resetTarget != null}
        title="Send password reset email?"
        message={resetTarget ? `We’ll send a password reset link to ${resetTarget}.` : undefined}
        warning={resetError}
        confirmLabel="Send reset email"
        confirmType="dark"
        isConfirming={resetMutation.isPending}
        onConfirm={requestReset}
        onCancel={closeConfirmation}
      />
    </>
  )
}

const WorkosEventsRow = ({ user }: { user: AdminUserLookupResponse }) => {
  const workosEventsUrl = user.workos_events_url ?? null
  const hasLinkedWorkosUser = user.workos_user_id != null
  const actionDescription = !hasLinkedWorkosUser
    ? 'This user does not have a linked WorkOS account yet.'
    : workosEventsUrl == null
      ? 'WorkOS dashboard links are not configured for this environment.'
      : "Open this user's WorkOS events timeline in a new tab."

  const openWorkosEvents = () => {
    if (!workosEventsUrl) return
    window.open(workosEventsUrl, '_blank', 'noopener,noreferrer')
  }

  return (
    <div className={tabStyles.accountAction}>
      <div className={tabStyles.actionCopy}>
        <span className={tabStyles.infoLabel}>WorkOS events</span>
        <span className={tabStyles.actionDescription}>{actionDescription}</span>
      </div>
      <Button
        title={
          <span className={tabStyles.actionButtonContent}>
            View in WorkOS
            <ExternalLink size={12} />
          </span>
        }
        onClick={openWorkosEvents}
        type="light"
        isDisabled={workosEventsUrl == null}
        className={tabStyles.rowAction}
      />
    </div>
  )
}

// Mirrors the backend's MAX_DICTIONARY_IMPORT_FILE_SIZE_BYTES so oversized files fail fast client-side.
const MAX_IMPORT_FILE_BYTES = 64 * 1024 * 1024

const IMPORT_BLOCKED_TOOLTIP =
  "This user's organization has disabled Dictionary & Snippets, so entries can't be imported."

const pluralize = (n: number, singular: string, plural: string) => `${n} ${n === 1 ? singular : plural}`

// The export is fetched as a Blob, so the backend's error detail isn't parsed; map by status instead.
const exportErrorMessage = (err: unknown) => {
  if (isApiError(err) && err.status === 413) {
    return 'This dictionary is too large to export in one file (10,000 entries / 64MB limit).'
  }
  if (isApiError(err) && err.status === 404) return 'User not found.'
  return "Couldn't export the dictionary. Try again."
}

const DictionaryTransferRow = ({ user }: { user: AdminUserLookupResponse }) => {
  const { updateToast } = useToast()
  const { trackEvent } = useAnalytics()
  const fileInputRef = useRef<HTMLInputElement>(null)
  // React Query pending state is asynchronous; this blocks same-tick duplicate clicks.
  const importInFlight = useRef(false)
  const [pendingFile, setPendingFile] = useState<File | null>(null)
  const [confirmText, setConfirmText] = useState('')
  const [importError, setImportError] = useState<string | null>(null)

  // The account the admin means to act on rides the mutation variables — the looked-up
  // `user` can change while a request is in flight, and latest-render callbacks would
  // otherwise label one account's data with another account's name.
  const exportMutation = useMutation({
    mutationFn: (target: AdminUserLookupResponse) => exportUserDictionaryCsv(target.user_id),
    onSuccess: (res, target) => {
      downloadBlob(res.data, `dictionary-${target.email ?? target.user_id}.csv`)
      trackEvent?.(EventName.ExportedUserDictionary, { user_id: target.user_id })
    },
    onError: (err) => updateToast(exportErrorMessage(err), 'error'),
  })

  const importMutation = useMutation({
    mutationFn: ({ target, file, confirm }: { target: AdminUserLookupResponse; file: File; confirm: string }) =>
      importUserDictionaryCsv(target.user_id, file, confirm),
    onSuccess: (res, { target }) => {
      const skippedNote =
        res.data.rows_skipped > 0
          ? ` (${pluralize(res.data.rows_skipped, 'duplicate row', 'duplicate rows')} skipped)`
          : ''
      updateToast(
        `Imported ${pluralize(res.data.rows_imported, 'dictionary entry', 'dictionary entries')} into ${target.email ?? target.user_id}${skippedNote}.`,
        'success'
      )
      trackEvent?.(EventName.ImportedUserDictionary, {
        user_id: target.user_id,
        rows_imported: res.data.rows_imported,
        rows_skipped: res.data.rows_skipped,
      })
      closeConfirmation()
    },
    onError: (err) => {
      setImportError(isApiError(err) ? (err.detail ?? err.message) : "Couldn't import the file. Try again.")
    },
    onSettled: () => {
      importInFlight.current = false
    },
  })

  const onFileChosen = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null
    e.target.value = ''
    if (!file) return
    if (file.size > MAX_IMPORT_FILE_BYTES) {
      updateToast('That file is larger than the 64MB import limit.', 'error')
      return
    }
    setImportError(null)
    setConfirmText('')
    setPendingFile(file)
  }

  const importBlocked = user.dictionary_import_blocked === true
  const importButton = (
    <Button
      title="Import CSV"
      onClick={() => fileInputRef.current?.click()}
      type="light"
      isDisabled={importBlocked || importMutation.isPending}
      className={tabStyles.rowAction}
    />
  )
  const targetLabel = user.email ?? user.user_id
  const typedConfirm = confirmText.trim().toLowerCase()
  const confirmMatchesTarget =
    typedConfirm !== '' && (typedConfirm === user.email?.toLowerCase() || typedConfirm === user.user_id.toLowerCase())

  const confirmImport = () => {
    if (!pendingFile || !confirmMatchesTarget || importInFlight.current) return
    importInFlight.current = true
    setImportError(null)
    importMutation.mutate({ target: user, file: pendingFile, confirm: confirmText.trim() })
  }

  const closeConfirmation = () => {
    setPendingFile(null)
    setConfirmText('')
    setImportError(null)
  }

  return (
    <>
      <div className={tabStyles.accountAction}>
        <div className={tabStyles.actionCopy}>
          <span className={tabStyles.infoLabel}>Dictionary & snippets</span>
          <span className={tabStyles.actionDescription}>
            Export this user&apos;s personal dictionary as a CSV, or restore an exported CSV onto this account (e.g.
            after an email change). Snippet formatting is included. Import only adds or updates entries; it never
            deletes.
          </span>
        </div>
        <Button
          title="Export CSV"
          onClick={() => exportMutation.mutate(user)}
          type="light"
          isLoading={exportMutation.isPending}
          isDisabled={exportMutation.isPending}
          className={tabStyles.rowAction}
        />
        {importBlocked ? (
          <div className={tabStyles.rowActionSlot}>
            <Tooltip text={IMPORT_BLOCKED_TOOLTIP} placement="top" hideArrow>
              {importButton}
            </Tooltip>
          </div>
        ) : (
          importButton
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,text/csv"
          onChange={onFileChosen}
          style={{ display: 'none' }}
        />
      </div>

      <ConfirmDialog
        isOpen={pendingFile != null}
        title="Import dictionary & snippets?"
        message={
          pendingFile ? (
            <>
              <p>
                Import &quot;{pendingFile.name}&quot; into <strong>{targetLabel}</strong>&apos;s account? Entries with
                the same spelling are overwritten; nothing is deleted.
              </p>
              <p>To confirm this is the right account, type its {user.email ? 'email address' : 'user ID'}:</p>
              <Input
                type="text"
                value={confirmText}
                onInput={(text) => setConfirmText(text)}
                placeholder={targetLabel}
              />
            </>
          ) : undefined
        }
        warning={importError}
        confirmLabel="Import"
        confirmType="dark"
        isConfirming={importMutation.isPending}
        isConfirmDisabled={!confirmMatchesTarget}
        onConfirm={confirmImport}
        onCancel={() => {
          if (!importMutation.isPending) closeConfirmation()
        }}
      />
    </>
  )
}

const ManageUserTab = () => {
  const [query, setQuery] = useState('')
  const [resultQuery, setResultQuery] = useState('')
  const [result, setResult] = useState<AdminUserLookupResponse | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const mutation = useMutation({
    mutationFn: (searchQuery: string) => adminLookupUser(searchQuery),
    onSuccess: (res, searchQuery) => {
      setResult(res.data)
      setResultQuery(searchQuery)
      setErrorMessage(null)
    },
    onError: (err) => {
      setResult(null)
      setErrorMessage(isApiError(err) ? (err.detail ?? err.message) : 'User not found')
    },
  })

  const handleSearch = () => {
    if (query.trim()) mutation.mutate(query.trim())
  }

  // Re-run the lookup so the panel reflects a just-applied Customer ID change.
  const refreshResult = async () => {
    if (resultQuery) await mutation.mutateAsync(resultQuery)
  }

  return (
    <div className={styles.formSection}>
      <div className={styles.formFields}>
        <h2 className={styles.sectionTitle}>Manage User</h2>
        <p className={styles.infoText}>
          Search for a user by email address or user ID to view and manage their information.
        </p>

        <div className={tabStyles.searchRow}>
          <input
            className={tabStyles.searchInput}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Enter email address or user ID"
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          />
          <Button
            title="Search"
            onClick={handleSearch}
            type="dark"
            isLoading={mutation.isPending}
            isDisabled={mutation.isPending || !query.trim()}
          />
        </div>

        {errorMessage && <p className={styles.error}>{errorMessage}</p>}
      </div>

      {result && (
        <div className={tabStyles.resultsContainer}>
          <div className={tabStyles.infoSection}>
            <div className={tabStyles.sectionHeader}>
              <h3 className={tabStyles.sectionTitle}>User Information</h3>
              {result.enterprise_id != null && (
                <span className={tabStyles.enterprisePill}>
                  <BuildingComplex size={13} />
                  Enterprise user
                </span>
              )}
            </div>
            <InfoRow label="First Name" value={result.first_name ?? '—'} />
            <InfoRow label="Last Name" value={result.last_name ?? '—'} />
            <InfoRow label="Email Address" value={result.email ?? '—'} />
            <InfoRow label="User ID" value={result.user_id} />
            {result.enterprise_id != null && (
              <>
                <InfoRow
                  label="Enterprise"
                  value={
                    result.enterprise_name ? (
                      <Link
                        className={tabStyles.enterpriseLink}
                        to={`/app/enterprises/manage?enterpriseId=${encodeURIComponent(result.enterprise_id)}&enterpriseName=${encodeURIComponent(result.enterprise_name)}`}
                      >
                        {result.enterprise_name}
                        <ExternalLink size={12} />
                      </Link>
                    ) : (
                      '—'
                    )
                  }
                />
                <InfoRow label="Enterprise ID" value={result.enterprise_id} />
                <InfoRow label="Enterprise Role" value={result.enterprise_role ?? '—'} />
              </>
            )}
            <InfoRow label="Referral Code" value={result.referral_code ?? '—'} />
            <InfoRow label="Referrer Code" value={result.referrer_code ?? '—'} />
          </div>

          <div className={tabStyles.infoSection}>
            <div className={tabStyles.sectionHeader}>
              <h3 className={tabStyles.sectionTitle}>Subscription Information</h3>
            </div>
            <StripeCustomerIdRow user={result} onApplied={refreshResult} />
            <InfoRow
              label="Stripe Subscription ID"
              value={isPaidPlan(result.plan_name) ? (result.stripe_subscription_id ?? '—') : 'N/A'}
            />
            <InfoRow label="Plan" value={result.plan_name ?? '—'} />
            <InfoRow label="Status" value={result.subscription_status ?? '—'} />
            <InfoRow label="Subscription Created" value={formatDate(result.subscription_created_at)} />
            <TrialEndDateRow
              key={result.user_id}
              user={result}
              formattedDate={formatDate(result.trial_ends_at, 'UTC')}
              onApplied={refreshResult}
            />
          </div>

          <div className={tabStyles.infoSection}>
            <div className={tabStyles.sectionHeader}>
              <h3 className={tabStyles.sectionTitle}>Account actions</h3>
            </div>
            <PasswordResetRow key={result.user_id} user={result} />
            <WorkosEventsRow user={result} />
            <DictionaryTransferRow user={result} />
          </div>
        </div>
      )}
    </div>
  )
}

export default ManageUserTab
